import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Cancel, ExpandMore, Search } from "@material-ui/icons";
import { api } from "../../../services/apiService";
import "./curriculum-filter-panel.scss";

/*
 * Curriculum browse/filter for ABSITE quiz creation (product/CONTRACT-UI-SLICE2.md).
 * Counts are raw numbers of questions with a REVIEWED curriculum mapping, never
 * coverage or mastery. A topic with none cannot be selected. Any load failure or a
 * dark (404) backend renders nothing, so the page behaves exactly as before.
 */
const NOT_AFFILIATED =
  "QLearn is not affiliated with or endorsed by SCORE or the American Board of Surgery.";

// The server rejects more rows than this (CreateQuizDto @Size(max = 200)).
export const MAX_SELECTED_TOPICS = 200;
// Subsection headings only earn their space in a category with several topics on show.
const MIN_TOPICS_FOR_SUBSECTIONS = 4;
// A QLearn grouping for outline parts that have no categories; not an outline part itself.
const OTHER_SECTION = "More outline sections";

// Module-level so the default loader is referentially stable: an inline default would
// be a new function every render and re-run the load effect in a loop.
const defaultLoadOutline = (edition) => api.getCurriculumOutline(edition);

// A category with at least this many topics selected is shown as one chip.
const MIN_TOPICS_FOR_CATEGORY_CHIP = 4;
const SEARCH_DEBOUNCE_MS = 150;

// The edition is an internal snapshot id, not an official edition name. A snapshot id
// reads as its retrieval date; anything else is shown as given.
export const editionLabel = (edition) => {
  const match = /^gs-outline-(\d{4})(\d{2})(\d{2})$/.exec(String(edition || ""));
  if (!match) return `edition ${edition}`;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const text = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  return `public outline retrieved ${text}`;
};

// "None matched yet", not "no questions": matching covers part of the bank, so a zero
// says nothing about how many questions the bank holds on the topic.
export const questionCountText = (count) => {
  if (!count) return "None matched yet";
  return count === 1 ? "1 question" : `${count} questions`;
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/*
 * Sections are the outline's parts. A part whose rows carry no category (the
 * non-clinical competencies) becomes a single category under one trailing section,
 * so the list never shows a heading with the same name twice.
 */
export const groupOutline = (rows) => {
  const sorted = [...rows].sort((a, b) => a.ordinal - b.ordinal);
  const partHasCategory = new Set(sorted.filter((row) => row.category).map((row) => row.part || ""));
  const sections = [];
  const sectionByName = new Map();
  const categoryByKey = new Map();
  sorted.forEach((row) => {
    const part = row.part || "";
    const sectionName = partHasCategory.has(part) ? part : OTHER_SECTION;
    const categoryName = row.category || part || "Topics";
    if (!sectionByName.has(sectionName)) {
      const section = { name: sectionName, categories: [] };
      sectionByName.set(sectionName, section);
      sections.push(section);
    }
    const key = `${sectionName}|${categoryName}`;
    if (!categoryByKey.has(key)) {
      const category = { key, name: categoryName, rows: [] };
      categoryByKey.set(key, category);
      sectionByName.get(sectionName).categories.push(category);
    }
    categoryByKey.get(key).rows.push(row);
  });
  // The catch-all section always reads last, wherever its first row sat in the outline.
  return [...sections.filter((s) => s.name !== OTHER_SECTION), ...sections.filter((s) => s.name === OTHER_SECTION)];
};

const hasQuestions = (row) => row.reviewedQuestionCount > 0;

export default function CurriculumFilterPanel({
  edition,
  selectedRowIds,
  onChange,
  // The page's exact pool size for the current filters, shown beside the selection.
  availableCount,
  // True when availableCount counts high-yield questions only (the page's High-Yield switch).
  highYieldOnly = false,
  // False while the builder form itself is hidden: nothing is fetched or shown then.
  enabled = true,
  loadOutline = defaultLoadOutline,
}) {
  const [rows, setRows] = useState(null);
  // Always starts closed. Reopening itself when the outline arrived (about a second after the
  // page) pushed the whole builder down by the height of the open section.
  const [expanded, setExpanded] = useState(false);
  // `query` is what the box shows; `appliedQuery` filters the list a moment later, so the list
  // is not rebuilt, nor the match count re-announced, on every keystroke.
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [openCategories, setOpenCategories] = useState(() => new Set());
  const [limit, setLimit] = useState(null);
  const showAllRef = useRef(null);
  const searchRef = useRef(null);
  const chipsRef = useRef(null);
  // The chip just removed: its index, and whether the keyboard removed it. Focus then moves to
  // its neighbour; only a keyboard user is sent to the search box when no chip is left, because
  // focusing a text field after a tap scrolls to it and opens the phone keyboard.
  const removedChip = useRef(null);

  // The latest selection and callback, read by the load effect without re-running it.
  const selectionRef = useRef({ selectedRowIds, onChange });
  selectionRef.current = { selectedRowIds, onChange };

  useEffect(() => {
    if (!enabled) {
      // A hidden form must not carry a filter nobody can see.
      const { selectedRowIds: current, onChange: report } = selectionRef.current;
      if (current && current.length) report([]);
      return undefined;
    }
    let active = true;
    const settle = (loaded) => {
      if (!active) return;
      setRows(loaded);
      // Only topics that are on show and selectable may stay selected: no invisible filter.
      const { selectedRowIds: current, onChange: report } = selectionRef.current;
      const selectable = new Set(loaded.filter(hasQuestions).map((row) => row.rowId));
      const kept = (current || []).filter((id) => selectable.has(id));
      if (current && kept.length !== current.length) report(kept);
    };
    Promise.resolve(loadOutline(edition))
      .then((result) => settle(Array.isArray(result) ? result : []))
      .catch(() => settle([]));
    return () => {
      active = false;
    };
  }, [edition, loadOutline, enabled]);

  // After a chip removes itself, keep keyboard focus on the next chip, or on the search box.
  useEffect(() => {
    if (removedChip.current === null) return;
    const { index, byKeyboard } = removedChip.current;
    removedChip.current = null;
    const buttons = chipsRef.current ? chipsRef.current.querySelectorAll(".curriculum-chip-remove") : [];
    if (buttons.length) buttons[Math.min(index, buttons.length - 1)].focus();
    else if (byKeyboard && searchRef.current) searchRef.current.focus();
  }, [selectedRowIds]);

  useEffect(() => {
    const timer = setTimeout(() => setAppliedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const sections = useMemo(() => (rows ? groupOutline(rows) : []), [rows]);
  const rowInfo = useMemo(() => {
    const info = new Map();
    sections.forEach((section) => section.categories.forEach((category) =>
      category.rows.forEach((row) => info.set(row.rowId, { title: row.title, category: category.name }))));
    return info;
  }, [sections]);
  const selected = useMemo(() => selectedRowIds || [], [selectedRowIds]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  if (!enabled) return null;
  // While the outline loads, the same collapsed header is shown (closed and inert), so it has
  // exactly the final header's size at every width and the builder below does not jump.
  const loading = rows === null;
  // Nothing to offer means no section at all, so the page behaves exactly as before.
  if (!loading && !rows.some(hasQuestions)) return null;

  const needle = appliedQuery.trim().toLowerCase();
  const matches = (row, category) =>
    !needle || (row.title || "").toLowerCase().includes(needle) || category.name.toLowerCase().includes(needle);
  const visibleRows = (category) =>
    category.rows.filter((row) => (showAll || hasQuestions(row)) && matches(row, category));
  const hiddenCount = showAll || loading ? 0 : rows.filter((row) => !hasQuestions(row)).length;
  const hiddenMatches = showAll ? 0 : sections.reduce((total, section) =>
    total + section.categories.reduce((sum, category) =>
      sum + category.rows.filter((row) => !hasQuestions(row) && matches(row, category)).length, 0), 0);

  const report = (next) => {
    // Only growth is refused, so a selection can always be reduced.
    if (next.length > MAX_SELECTED_TOPICS && next.length > selected.length) {
      setLimit({ selected: selected.length, adding: next.length - selected.length });
      return;
    }
    setLimit(null);
    onChange(next);
  };
  const toggleTopic = (rowId) =>
    report(selectedSet.has(rowId) ? selected.filter((id) => id !== rowId) : [...selected, rowId]);
  const toggleRows = (ids) => {
    const allSelected = ids.every((id) => selectedSet.has(id));
    report(allSelected
      ? selected.filter((id) => !ids.includes(id))
      : [...selected, ...ids.filter((id) => !selectedSet.has(id))]);
  };
  const toggleOpen = (key) =>
    setOpenCategories((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const stop = (event) => event.stopPropagation();

  const visibleSections = sections
    .map((section) => ({
      ...section,
      categories: section.categories.filter((category) => visibleRows(category).length > 0),
    }))
    .filter((section) => section.categories.length > 0);

  // One chip for a category that is wholly selected, or has several topics selected, so a
  // large category does not bury the form under dozens of chips.
  const chips = [];
  const inCategoryChip = new Set();
  sections.forEach((section) => section.categories.forEach((category) => {
    const all = category.rows.filter(hasQuestions).map((row) => row.rowId);
    const ids = all.filter((id) => selectedSet.has(id));
    const whole = all.length >= 2 && ids.length === all.length;
    if (whole || ids.length >= MIN_TOPICS_FOR_CATEGORY_CHIP) {
      chips.push({ key: `category:${category.key}`,
        label: whole ? `${category.name} · all ${all.length}` : `${category.name} · ${ids.length} of ${all.length}`,
        title: `${whole ? "All" : ids.length} ${whole ? all.length : `of ${all.length}`} topics with questions in ${category.name}`,
        removeLabel: `Remove ${whole ? "all " : ""}${ids.length} ${category.name} topics`, ids });
      ids.forEach((id) => inCategoryChip.add(id));
    }
  }));
  const titleCounts = new Map();
  rowInfo.forEach(({ title }) => titleCounts.set(title, (titleCounts.get(title) || 0) + 1));
  selected.filter((id) => rowInfo.has(id) && !inCategoryChip.has(id)).forEach((id) => {
    const { title, category } = rowInfo.get(id);
    // The outline repeats a few titles in different categories; those chips name the category.
    const label = titleCounts.get(title) > 1 ? `${title} (${category})` : title;
    chips.push({ key: id, label, title: `${title} (${category})`, removeLabel: `Remove ${title} (${category})`, ids: [id] });
  });
  const removeChip = (chip, index, event) => {
    // A click with detail 0 came from the keyboard (Enter or Space on the button).
    removedChip.current = { index, byKeyboard: !!event && event.detail === 0 };
    report(selected.filter((id) => !chip.ids.includes(id)));
  };
  const matchCount = visibleSections.reduce((total, section) =>
    total + section.categories.reduce((sum, category) => sum + visibleRows(category).length, 0), 0);

  const hasCount = Number.isInteger(availableCount) && availableCount >= 0;
  let summaryText = "Optional · narrow to specific topics";
  if (loading) {
    summaryText = "Loading topics…";
  } else if (selected.length) {
    // Named for what it counts, so a number smaller than the rows' own counts explains itself.
    let countText = "";
    if (hasCount && highYieldOnly) {
      countText = availableCount === 0 ? "no High-Yield questions"
        : plural(availableCount, "High-Yield question", "High-Yield questions");
    } else if (hasCount) {
      countText = `${plural(availableCount, "question", "questions")} available`;
    }
    summaryText = `${plural(selected.length, "topic", "topics")} selected${countText ? ` · ${countText}` : ""}`;
  }
  const hiddenNote = needle ? hiddenMatches : hiddenCount;

  return (
    <Accordion
      className="quiz-selections curriculum-filter-panel"
      aria-busy={loading}
      expanded={expanded}
      onChange={(_event, isExpanded) => {
        if (loading) return;
        setExpanded(isExpanded); setLimit(null);
      }}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-curriculum-content"
        id="panel-curriculum-header"
      >
        {/* Names the outline residents are assigned from, so they recognise these as their
            curriculum topics. The source line and non-affiliation notice are in the open section. */}
        <Typography className="header curriculum-heading">Surgery Topics (SCORE outline)</Typography>
        <span className="curriculum-preview-tag">Early preview</span>
        {/* Always rendered, so the header keeps one height whether or not topics are selected. */}
        <Button
          disableElevation
          color="primary"
          className="curriculum-clear"
          onClick={(event) => {
            stop(event);
            report([]);
            // The button hides itself; keep keyboard focus on the section rather than losing it.
            document.getElementById("panel-curriculum-header")?.focus();
          }}
          onFocus={stop}
          disabled={selected.length === 0}
          aria-hidden={selected.length === 0}
          tabIndex={selected.length === 0 ? -1 : 0}
          style={selected.length === 0 ? { visibility: "hidden" } : undefined}
        >
          Clear All
        </Button>
        <Typography
          className={`curriculum-summary${selected.length ? " is-active" : ""}`}
          data-testid="curriculum-summary"
        >
          {summaryText}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className="accordian-summary curriculum-body">
        <Typography className="curriculum-status">
          Counts show the questions matched to each topic so far. Your quiz can offer fewer: it draws
          only from questions that are ready for practice and fit your other filters.
        </Typography>
        <div className="curriculum-controls">
          <TextField
            className="curriculum-search"
            type="search"
            size="small"
            variant="outlined"
            placeholder="Search topics, e.g. trauma"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputRef={searchRef}
            inputProps={{ "aria-label": "Search curriculum topics" }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
          />
          <FormControlLabel
            className="curriculum-show-all"
            control={<Switch size="small" color="primary" checked={showAll} inputRef={showAllRef}
              onChange={(event) => setShowAll(event.target.checked)} />}
            label="Show topics without matched questions"
          />
        </div>
        {/* Announces the result of a search to screen readers; nothing is shown. */}
        <span className="curriculum-sr-only" role="status" aria-live="polite">
          {needle ? `${matchCount} ${matchCount === 1 ? "topic matches" : "topics match"}` : ""}
        </span>
        {limit && (
          <Typography role="alert" className="curriculum-limit">
            You can select up to {MAX_SELECTED_TOPICS} topics. {limit.selected} are selected; this would
            add {limit.adding}.
          </Typography>
        )}
        {hiddenNote > 0 && (
          <Typography className="curriculum-hidden-note">
            {needle
              ? `${plural(hiddenNote, "matching topic has", "matching topics have")} no matched questions yet.`
              : `${plural(hiddenNote, "more topic has", "more topics have")} no matched questions yet.`}{" "}
            <button type="button" className="curriculum-link"
              onClick={() => { setShowAll(true); showAllRef.current?.focus(); }}>
              Show them
            </button>
          </Typography>
        )}
        <div className={`curriculum-list${needle ? " is-searching" : ""}`} role="group" aria-label="Curriculum topics">
          {visibleSections.length === 0 && (
            <Typography className="curriculum-empty">
              No {showAll ? "topics" : "topics with matched questions"} match “{appliedQuery.trim()}”. Names follow
              the SCORE outline; try a broader term or browse the list.
            </Typography>
          )}
          {visibleSections.map((section) => (
            <section key={section.name} className="curriculum-section">
              <Typography component="h2" className="curriculum-section-name">{section.name}</Typography>
              {section.categories.map((category) => {
                const shown = visibleRows(category);
                // While searching, the category box acts on the listed matches only, never on
                // topics the search is hiding.
                const scope = (needle ? shown : category.rows).filter(hasQuestions);
                const chosen = scope.filter((row) => selectedSet.has(row.rowId)).length;
                const partly = chosen > 0 && chosen < scope.length;
                const open = !!needle || openCategories.has(category.key);
                const withSubsections = shown.length >= MIN_TOPICS_FOR_SUBSECTIONS;
                const qualifier = showAll ? " with questions" : "";
                const unit = needle ? (scope.length === 1 ? " match" : " matches") : "";
                const meta = chosen > 0
                  ? `${chosen} of ${scope.length}${unit}${qualifier} selected`
                  : `${needle ? `${scope.length}${unit}` : plural(scope.length, "topic", "topics")}${qualifier}`;
                let lastSubsection = null;
                const label = (
                  <>
                    <span className="curriculum-category-name">{category.name}</span>
                    <span className="curriculum-category-meta">{meta}</span>
                  </>
                );
                return (
                  <div key={category.key} className="curriculum-category">
                    <div className="curriculum-category-row">
                      <Checkbox
                        color="primary"
                        disabled={scope.length === 0}
                        checked={scope.length > 0 && chosen === scope.length}
                        indeterminate={partly}
                        onChange={() => toggleRows(scope.map((row) => row.rowId))}
                        // The native property is what exposes the mixed state to assistive technology.
                        inputRef={(input) => { if (input) input.indeterminate = partly; }}
                        inputProps={{
                          "aria-label": `Select ${needle ? "the matching" : "all"} topics with questions in ${category.name}`,
                        }}
                      />
                      {needle ? (
                        // While searching every match is open, so the row is a label, not a control.
                        <div className="curriculum-category-toggle is-static">{label}</div>
                      ) : (
                        <button
                          type="button"
                          className="curriculum-category-toggle"
                          data-testid="curriculum-group-toggle"
                          aria-expanded={open}
                          onClick={() => toggleOpen(category.key)}
                        >
                          {label}
                          <ExpandMore className={`curriculum-chevron${open ? " is-open" : ""}`} fontSize="small" />
                        </button>
                      )}
                    </div>
                    <Collapse in={open} unmountOnExit>
                      <ul className="curriculum-topics">
                        {shown.map((row) => {
                          const none = !hasQuestions(row);
                          const heading = withSubsections && row.subsection && row.subsection !== lastSubsection
                            ? row.subsection : null;
                          lastSubsection = row.subsection || lastSubsection;
                          return (
                            <React.Fragment key={row.rowId}>
                              {heading && <li className="curriculum-subsection">{heading}</li>}
                              <li data-testid="curriculum-topic" className={`curriculum-topic${none ? " is-empty" : ""}`}>
                                <label className="curriculum-topic-label">
                                  <Checkbox
                                    color="primary"
                                    size="small"
                                    disabled={none}
                                    checked={selectedSet.has(row.rowId)}
                                    onChange={() => toggleTopic(row.rowId)}
                                  />
                                  <span className="curriculum-topic-title">{row.title}</span>
                                  <span className="curriculum-count">{questionCountText(row.reviewedQuestionCount)}</span>
                                </label>
                              </li>
                            </React.Fragment>
                          );
                        })}
                      </ul>
                    </Collapse>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="curriculum-selected">
            <Typography className="curriculum-selected-label" id="curriculum-selected-label">
              Selected ({selected.length})
            </Typography>
            <ul className="curriculum-chips" aria-labelledby="curriculum-selected-label" ref={chipsRef}>
              {chips.map((chip, index) => (
                <li key={chip.key} className="curriculum-chip" title={chip.title}>
                  <span className="curriculum-chip-label">{chip.label}</span>
                  <button type="button" className="curriculum-chip-remove"
                    aria-label={chip.removeLabel} onClick={(event) => removeChip(chip, index, event)}>
                    <Cancel fontSize="small" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Typography className="curriculum-filter-note">
          Topics follow the SCORE® General Surgery Curriculum Outline ({editionLabel(edition)}). {NOT_AFFILIATED}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
